import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ticketsApi } from "../lib/events";
import { extractError } from "../lib/api";
import { Logo } from "../components/Logo";

export default function ResendTickets() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter the email you used at checkout.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await ticketsApi.resend(email.trim());
      setResult(r);
      toast.success(r.message || "Check your inbox.");
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)]" data-testid="resend-page">
      <nav className="gt-glass-nav">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <Link to="/" className="gt-btn-ghost text-sm" data-testid="resend-home-link">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-6 sm:px-10 py-14 sm:py-20">
        <div className="gt-label mb-3">Resend tickets</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
          Lost your email?
        </h1>
        <p className="text-[var(--gt-ink-2)] mb-8">
          Enter the email you used at checkout and we&apos;ll re-send every valid ticket linked to it.
        </p>

        {result ? (
          <div className="gt-card" data-testid="resend-success">
            <CheckCircle2 size={32} className="text-[var(--gt-accent)] mb-3" strokeWidth={1.5} />
            <div className="gt-label mb-1">Done</div>
            <h2 className="font-heading text-2xl font-black tracking-tight mb-2">
              {result.orders_resent > 0
                ? `${result.orders_resent} order(s) resent`
                : "Check your inbox"}
            </h2>
            <p className="text-sm text-[var(--gt-ink-2)] mb-4">{result.message}</p>

            {result.dev_previews?.length > 0 && (
              <div className="mt-4 border-t border-[var(--gt-line)] pt-4">
                <div className="gt-label mb-2">Dev preview</div>
                <ul className="space-y-2 text-xs">
                  {result.dev_previews.map((p) => (
                    <li key={p.email_log_id}>
                      <a
                        href={p.preview_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2 font-mono break-all"
                        data-testid={`dev-preview-${p.email_log_id}`}
                      >
                        {p.preview_url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              onClick={() => { setResult(null); setEmail(""); }}
              className="mt-6 gt-btn-ghost text-sm"
              data-testid="resend-another-btn"
            >
              Resend for a different email
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="gt-card space-y-4" data-testid="resend-form">
            <div>
              <label className="gt-form-label">Email address</label>
              <input
                type="email"
                required
                className="gt-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@youraddress.com.au"
                data-testid="resend-email-input"
              />
            </div>
            {error && (
              <div className="text-sm text-[var(--gt-accent)] font-medium" data-testid="resend-error">{error}</div>
            )}
            <button
              type="submit"
              className="gt-btn-primary w-full justify-center"
              disabled={submitting}
              data-testid="resend-submit-btn"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Resend my tickets
            </button>
            <p className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] text-center pt-2">
              We don&apos;t confirm whether an email exists in our system.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
